import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import styles from './CreateCompanyModal.module.css';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectCompanies } from '@mm/state/selectors/viewSelectors';
import TextInput from '../../components/TextInput';
import { CloseOutlined } from '@ant-design/icons';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';

export const CreateCompanyModal = () => {
  const session = useSelector((state: StoreState) => state.session.session);
  const companies = useSelector(selectCompanies);

  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { create } = useCompanyCRUDActions();
  const { updateActiveCompanyId } = useUsersCRUDActions();
  const { closeModal } = useModalActions();

  const isDisabled =
    companyName.length === 0 || !companyName.trim() || /^\s/.test(companyName);
  const InputClass = classnames('company-panel__input', styles.input);

  const onKeyDown = e => {
    if (e.key === ENTER_KEY_NAME && companyName.length && !isDisabled) {
      onCreateCompany();
    }
  };

  const onCreateCompany = async () => {
    const existingCompany = companies.find(
      company => company.name === companyName,
    );
    if (!existingCompany) {
      const companyCreated = await create({ name: companyName });

      if (companyCreated) {
        await updateActiveCompanyId(session.id, companyCreated.company.id);
        closeModal();
        setError(null);
      } else {
        setError('Failed to create company');
      }
    } else {
      setError('Company with this name exists');
    }
  };

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.close} onClick={() => closeModal()} />
      <div className={styles.header}>Create New Company </div>
      <div className={styles.inputWrapper}>
        <div className={styles.inputName}>Company name</div>
        <TextInput
          className={InputClass}
          value={companyName}
          placeholder="Enter Company Name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCompanyName(e.target.value)
          }
          style={{ maxWidth: '100%' }}
          maxLength={50}
          autoFocus
          onKeyDown={onKeyDown}
        />
        {error && <div className={styles.error}>{error}</div>}
      </div>
      <div className={styles.buttons}>
        <PrimaryButton
          color="transparent-no-shadow"
          className={styles.secondaryButton}
          onClick={() => closeModal()}
        >
          Cancel
        </PrimaryButton>
        <PrimaryButton
          className={styles.primaryButton}
          onClick={onCreateCompany}
          disabled={isDisabled}
        >
          Create Company
        </PrimaryButton>
      </div>
    </div>
  );
};
